import { getEnv } from '../utils/env';

const NEXAR_API_URL = getEnv('VITE_NEXAR_API_URL') || 'https://api.nexar.com/graphql';
const NEXAR_TOKEN = getEnv('VITE_NEXAR_TOKEN') || '';

export interface NexarPart {
    mpn: string; // Manufacturer Part Number
    manufacturer: {
        name: string;
    };
    shortDescription: string;
    category?: {
        name: string;
    };
    specs?: Array<{
        attribute: { name: string };
        value: string;
    }>;
    sellers?: Array<{
        company: { name: string };
        offers: Array<{
            sku: string;
            inventoryLevel: number;
            prices: Array<{
                quantity: number;
                price: number;
                currency: string;
            }>;
            clickUrl: string;
        }>;
    }>;
    octopartUrl?: string;
    datasheets?: Array<{
        url: string;
        name: string;
    }>;
}

export interface NexarSearchResult {
    parts: NexarPart[];
    totalResults: number;
}

/**
 * Search for components using Nexar/Octopart API
 */
export const searchComponents = async (query: string, limit: number = 10): Promise<NexarSearchResult> => {
    if (!NEXAR_TOKEN) {
        throw new Error('Nexar API token not configured. Please add VITE_NEXAR_TOKEN to .env.local');
    }

    const graphqlQuery = `
        query SearchParts($query: String!, $limit: Int!) {
            supSearch(q: $query, limit: $limit) {
                results {
                    part {
                        mpn
                        manufacturer {
                            name
                        }
                        shortDescription
                        category {
                            name
                        }
                        specs {
                            attribute {
                                name
                            }
                            value
                        }
                        sellers {
                            company {
                                name
                            }
                            offers {
                                sku
                                inventoryLevel
                                prices {
                                    quantity
                                    price
                                    currency
                                }
                                clickUrl
                            }
                        }
                    }
                }
            }
        }
    `;

    try {
        const response = await fetch(NEXAR_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${NEXAR_TOKEN}`,
            },
            body: JSON.stringify({
                query: graphqlQuery,
                variables: {
                    query,
                    limit
                }
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Nexar API error: ${response.status} - ${errorText}`);
        }

        const data = await response.json();

        if (data.errors) {
            throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
        }

        const parts = data.data?.supSearch?.results?.map((r: any) => r.part) || [];

        return {
            parts,
            totalResults: parts.length
        };

    } catch (error: any) {
        console.error('Nexar search error:', error);
        throw error;
    }
};

/**
 * Get detailed part information by MPN
 */
export const getPartDetails = async (mpn: string, manufacturer?: string): Promise<NexarPart | null> => {
    if (!NEXAR_TOKEN) {
        throw new Error('Nexar API token not configured');
    }

    const graphqlQuery = `
        query GetPartDetails($mpn: String!) {
            supSearchMpn(q: $mpn, limit: 1) {
                results {
                    part {
                        mpn
                        manufacturer {
                            name
                        }
                        shortDescription
                        category {
                            name
                        }
                        specs {
                            attribute {
                                name
                            }
                            value
                        }
                        sellers {
                            company {
                                name
                            }
                            offers {
                                sku
                                inventoryLevel
                                prices {
                                    quantity
                                    price
                                    currency
                                }
                                clickUrl
                            }
                        }
                    }
                }
            }
        }
    `;

    try {
        const response = await fetch(NEXAR_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${NEXAR_TOKEN}`,
            },
            body: JSON.stringify({
                query: graphqlQuery,
                variables: { mpn }
            })
        });

        if (!response.ok) {
            throw new Error(`Nexar API error: ${response.status}`);
        }

        const data = await response.json();

        if (data.errors) {
            throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
        }

        const part = data.data?.supSearchMpn?.results?.[0]?.part || null;

        // Filter by manufacturer if specified
        if (part && manufacturer && part.manufacturer.name.toLowerCase() !== manufacturer.toLowerCase()) {
            return null;
        }

        return part;

    } catch (error: any) {
        console.error('Nexar part details error:', error);
        throw error;
    }
};

/**
 * Get pricing and availability for a part
 */
export const getPartPricing = (part: NexarPart): {
    cheapestPrice: number;
    currency: string;
    totalStock: number;
    sellers: number;
} => {
    let cheapestPrice = Infinity;
    let currency = 'USD';
    let totalStock = 0;
    const sellerSet = new Set<string>();

    part.sellers?.forEach(seller => {
        sellerSet.add(seller.company.name);
        seller.offers.forEach(offer => {
            totalStock += offer.inventoryLevel || 0;
            offer.prices.forEach(price => {
                if (price.price < cheapestPrice) {
                    cheapestPrice = price.price;
                    currency = price.currency;
                }
            });
        });
    });

    return {
        cheapestPrice: cheapestPrice === Infinity ? 0 : cheapestPrice,
        currency,
        totalStock,
        sellers: sellerSet.size
    };
};

/**
 * Format part number for BOM import
 */
export const formatForBOM = (part: NexarPart): {
    partNumber: string;
    description: string;
    manufacturer: string;
    category: string;
    datasheetUrl?: string;
    estimatedCost?: number;
} => {
    const pricing = getPartPricing(part);

    return {
        partNumber: part.mpn,
        description: part.shortDescription || `${part.manufacturer.name} ${part.mpn}`,
        manufacturer: part.manufacturer.name,
        category: part.category?.name || 'Electronic Component',
        datasheetUrl: part.datasheets?.[0]?.url,
        estimatedCost: pricing.cheapestPrice > 0 ? pricing.cheapestPrice : undefined
    };
};

/**
 * Check if Nexar API is configured
 */
export const isNexarConfigured = (): boolean => {
    return Boolean(NEXAR_TOKEN && NEXAR_API_URL);
};

/**
 * Parse JWT token expiration
 */
export const getTokenExpiration = (): Date | null => {
    if (!NEXAR_TOKEN) return null;

    try {
        const payload = NEXAR_TOKEN.split('.')[1];
        const decoded = JSON.parse(atob(payload));
        return new Date(decoded.exp * 1000);
    } catch {
        return null;
    }
};

/**
 * Check if token is expired or will expire soon
 */
export const isTokenExpiring = (hoursThreshold: number = 24): boolean => {
    const expiration = getTokenExpiration();
    if (!expiration) return true;

    const now = new Date();
    const hoursUntilExpiration = (expiration.getTime() - now.getTime()) / (1000 * 60 * 60);

    return hoursUntilExpiration < hoursThreshold;
};
